import { yupResolver } from '@hookform/resolvers/yup';
import { Image, Link, LinkSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { FormDialog, FormError, FormSelect } from '@vizit/forms';
import { ArrowNarrowUpRightIcon, PuzzlePiece02Icon } from '@vizit/icons';
import { DialogHeaderStatus, DialogHeaderVariant, SelectItem } from '@vizit/molecules';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { AmazonToVizitRow } from '../amazon-to-vizit-row';
import { SCORE_PDP_ERROR_MESSAGES, ScorePDPErrorType } from '../score-pdp-dialog';

const formSchema = yup.object({
  workspaceId: yup.string().required('Workspace is required'),
});

type VendorProductImportFormSchema = yup.InferType<typeof formSchema>;

interface VendorProductImportWorkspace {
  id: string;
  name: string;
}

interface VendorProductImportProduct {
  name: string;
  asin: string;
  imageUrl?: string | null;
}

interface VendorProductImportDialogProps {
  product: VendorProductImportProduct;
  workspaces: VendorProductImportWorkspace[];
  isOpen: boolean;
  isImported?: boolean;
  errorType?: ScorePDPErrorType;
  onOpenChange: (isOpen: boolean) => void;
  onWorkspaceChange: (workspaceId: string) => void;
  onOpenDuplicate?: () => void;
  onSubmit: () => Promise<void>;
}

function VendorProductImportDialog(props: VendorProductImportDialogProps) {
  const { product, workspaces, isOpen, isImported = false, errorType = ScorePDPErrorType.None } = props;
  const { onOpenChange, onWorkspaceChange, onOpenDuplicate, onSubmit } = props;

  const defaultWorkspaceId = workspaces.length === 1 ? workspaces[0].id : '';

  const methods = useForm<VendorProductImportFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: { workspaceId: defaultWorkspaceId },
    mode: 'all',
  });

  const { control, formState, handleSubmit, reset } = methods;

  const selectedWorkspaceId = useWatch({ control, name: 'workspaceId' });
  React.useEffect(() => {
    if (selectedWorkspaceId) {
      onWorkspaceChange(selectedWorkspaceId);
    }
  }, [selectedWorkspaceId, onWorkspaceChange]);

  React.useEffect(() => {
    if (defaultWorkspaceId) {
      reset({ workspaceId: defaultWorkspaceId });
    }
  }, [defaultWorkspaceId, reset]);

  const handleOnResetForm = () => {
    reset({ workspaceId: defaultWorkspaceId });
  };

  return (
    <FormDialog
      formName="vendor-product-import"
      formMethods={methods}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
      headerVariant={DialogHeaderVariant.Icon}
      headerStatus={DialogHeaderStatus.Neutral}
      headerIcon={PuzzlePiece02Icon}
      data-testid="vendor-product-import-dialog"
    >
      <FormDialog.Title>Import product from Amazon Vendor Central</FormDialog.Title>
      <FormDialog.Subtitle>
        This product will be added to your library and scored against the imagery currently in Vendor Central. Product
        page is ready in a few minutes.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-8">
          <AmazonToVizitRow />

          <div className="flex items-center gap-6 border border-secondary p-6">
            <div className="h-24 w-24 shrink-0">
              <Image
                src={product.imageUrl ?? null}
                name={product.name}
                isBroken={!product.imageUrl}
                shape="rectangle"
                contain={false}
                className="h-full w-full"
                imageClassName="object-cover"
                isThumbnail
              />
            </div>
            <div className="flex min-w-0 flex-col">
              <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
                {product.name}
              </Text>
              <Text size={TextSize.Xs} color={TextColor.Tertiary}>
                {product.asin}
              </Text>
            </div>
          </div>

          {workspaces.length > 1 && !isImported && (
            <FormSelect
              control={control}
              name="workspaceId"
              label="Import to..."
              placeholder="Choose Workspace"
              isRequired
            >
              {workspaces.map((workspace) => (
                <SelectItem key={workspace.id} id={workspace.id} textValue={workspace.name}>
                  {workspace.name}
                </SelectItem>
              ))}
            </FormSelect>
          )}

          {isImported || formState.isSubmitting ? (
            <Text elementType="output" size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
              {isImported
                ? 'Imported into your library. Its scores are ready in a few minutes.'
                : 'Importing this product into your library. This can take a moment.'}
            </Text>
          ) : (
            <FormError message={SCORE_PDP_ERROR_MESSAGES[errorType]} />
          )}

          {onOpenDuplicate && !isImported && (
            <Link size={LinkSize.Sm} onPress={onOpenDuplicate} aria-label="Open existing product">
              Open product
            </Link>
          )}
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton
        data-testid="vendor-product-import-submit"
        iconEnd={isImported ? <ArrowNarrowUpRightIcon size={20} /> : undefined}
      >
        {isImported ? 'Open product' : 'Import to Library'}
      </FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { VendorProductImportDialog };
export type { VendorProductImportDialogProps, VendorProductImportProduct, VendorProductImportWorkspace };
